/**
 * The filesystem port.
 *
 * Services are written against these interfaces, never against the File System
 * Access API directly. That keeps DOM types out of the layers above and lets the
 * whole scan/export pipeline run in Node against a real folder during testing.
 *
 * Two implementations exist: BrowserFsDirectory (wraps FileSystemDirectoryHandle)
 * and, in the test suite, one backed by node:fs.
 */

export interface FsFile {
  name: string;
  size: number;
  /** Epoch milliseconds. */
  lastModified: number;
  bytes(): Promise<Uint8Array>;
  /** A Blob for the browser to decode; Node implementations may synthesise one. */
  blob(): Promise<Blob>;
}

export type FsEntry =
  | { kind: 'file'; name: string }
  | { kind: 'directory'; name: string };

export interface FsDirectory {
  readonly name: string;

  /** Immediate children, unsorted. */
  list(): Promise<FsEntry[]>;

  /** Open a subdirectory by name. Creates it when `create` is true. */
  directory(name: string, create?: boolean): Promise<FsDirectory>;

  /** Open a file by path relative to this directory ("burst/_DSC0101.JPG"). */
  file(relPath: string): Promise<FsFile>;

  /** True when `relPath` resolves to a readable file. */
  has(relPath: string): Promise<boolean>;

  /** Write bytes as a new file in this directory. */
  write(name: string, bytes: Uint8Array): Promise<void>;
}

export class FileMissingError extends Error {
  constructor(readonly relPath: string) {
    super(`File not found: ${relPath}`);
    this.name = 'FileMissingError';
  }
}

/** Split "burst/_DSC0101.JPG" into ["burst"], "_DSC0101.JPG". */
export function splitPath(relPath: string): { dirs: string[]; filename: string } {
  const parts = relPath.split('/').filter(Boolean);
  const filename = parts.pop() ?? '';
  return { dirs: parts, filename };
}

/** Add " (2)", " (3)" … before the extension until the name is free. */
export function uniqueName(name: string, taken: Set<string>): string {
  if (!taken.has(name)) return name;

  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';

  for (let n = 2; n < 10_000; n++) {
    const candidate = `${stem} (${n})${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${stem} (${Date.now()})${ext}`;
}
